import * as React from 'react'
import { Link } from 'react-router-dom'
import { FileText, Lock, Plus, Trash2 } from 'lucide-react'
import { sb } from '@/lib/supabase'
import { useAuth } from '@/auth/auth-provider'
import { useQuery } from '@/hooks/use-query'
import { formatDate } from '@/lib/format'
import { monthInputValue, monthLabel, suggestedReportTitle } from '@/lib/report'
import type { Report } from '@/lib/types'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { EmptyState, ErrorNote, Loading, Page } from '@/components/page'
import { useDirty, useDiscardGuard } from '@/hooks/use-unsaved'
import { DiscardPrompt } from '@/components/discard-prompt'

/**
 * The monthly reports list.
 *
 * Audience is narrower than any other page: leads and DPL staff, never a plain
 * member — and only a DPL coach can create or edit one. The route is registered
 * behind `isLead` and this page hides what a lead can't do, but the control is
 * `migrations/0018_reports.sql`; RLS refuses the write and returns no draft rows
 * regardless of what the SPA renders.
 */
export function ReportsPage() {
  const { currentTeam, isCoach } = useAuth()
  const teamId = currentTeam?.id
  const [creating, setCreating] = React.useState(false)

  const { data, loading, error, refetch } = useQuery(async () => {
    if (!teamId) return [] as Report[]
    const { data: rows, error: err } = await sb()
      .from('reports')
      .select('*')
      .eq('team_id', teamId)
      .order('period_month', { ascending: false })
    if (err) throw err
    return (rows ?? []) as Report[]
  }, [teamId])

  async function remove(r: Report) {
    if (!window.confirm(`Delete “${r.title}”? This can't be undone.`)) return
    const { error: err } = await sb().from('reports').delete().eq('id', r.id)
    if (err) return window.alert(err.message)
    void refetch()
  }

  const reports = data ?? []

  return (
    <Page
      title="Reports"
      description={
        isCoach
          ? 'One write-up per month: your introduction, the month’s metrics, where the evaluations stand, and your conclusion. Visible to the team lead once published.'
          : 'Monthly write-ups from your Don’t Panic Labs coach.'
      }
      actions={
        isCoach && (
          <Button onClick={() => setCreating(true)}>
            <Plus /> New report
          </Button>
        )
      }
    >
      {error && <ErrorNote error={error} />}

      {loading && !data ? (
        <Loading />
      ) : reports.length === 0 ? (
        <EmptyState
          icon={FileText}
          title={isCoach ? 'No reports yet' : 'No reports published yet'}
          description={
            isCoach
              ? 'Start one for the month you want to write up. The metrics and evaluations fill themselves in; you write the introduction and the conclusion.'
              : 'Your coach hasn’t published a monthly report for this team yet.'
          }
          action={
            isCoach && (
              <Button onClick={() => setCreating(true)}>
                <Plus /> Start a report
              </Button>
            )
          }
        />
      ) : (
        <div className="space-y-2.5">
          {reports.map((r) => (
            <Card key={r.id} className="transition-colors hover:border-primary/40">
              <CardContent className="flex flex-wrap items-center gap-4 p-4">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Link to={`/reports/${r.id}`} className="font-medium hover:underline">
                      {r.title}
                    </Link>
                    <Badge
                      variant={r.status === 'published' ? 'success' : 'muted'}
                      className="text-[10px]"
                    >
                      {r.status}
                    </Badge>
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                    <span className="font-medium">{monthLabel(r.period_month)}</span>
                    {r.status === 'published' ? (
                      <span>Published {formatDate(r.published_at)}</span>
                    ) : (
                      <span className="inline-flex items-center gap-1">
                        <Lock className="h-3 w-3" /> Draft — only Don&apos;t Panic Labs can see this
                      </span>
                    )}
                    {r.subtitle && <span className="truncate">{r.subtitle}</span>}
                  </div>
                </div>

                {isCoach && (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => void remove(r)}
                    aria-label={`Delete ${r.title}`}
                  >
                    <Trash2 />
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {creating && teamId && (
        <NewReportDialog
          teamId={teamId}
          existing={reports}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false)
            void refetch()
          }}
        />
      )}
    </Page>
  )
}

function NewReportDialog({
  teamId,
  existing,
  onClose,
  onSaved,
}: {
  teamId: string
  existing: Report[]
  onClose: () => void
  onSaved: () => void
}) {
  // Default to the most recent month that doesn't already have a report, which
  // is usually last month — the one you write up once it's over.
  const [month, setMonth] = React.useState(() => {
    const taken = new Set(existing.map((r) => monthInputValue(r.period_month)))
    const now = new Date()
    for (let back = 0; back < 24; back++) {
      const d = new Date(now.getFullYear(), now.getMonth() - back, 1)
      const v = monthInputValue(d)
      if (!taken.has(v)) return v
    }
    return monthInputValue(now)
  })
  const [title, setTitle] = React.useState('')
  const [subtitle, setSubtitle] = React.useState('')
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const guard = useDiscardGuard(useDirty({ month, title, subtitle }), onClose)

  const suggested = suggestedReportTitle(month)
  const clash = existing.some((r) => monthInputValue(r.period_month) === month)

  async function save(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const { error: err } = await sb().from('reports').insert({
        team_id: teamId,
        // The month input gives `YYYY-MM`; the column is a date and the trigger
        // normalizes it to the first of the month either way.
        period_month: `${month}-01`,
        title: title.trim() || suggested,
        subtitle: subtitle.trim() || null,
        status: 'draft',
      })
      if (err) throw err
      onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && guard.requestClose()}>
      <DialogContent className="sm:max-w-lg">
        <DiscardPrompt guard={guard} what="this report" />
        <form onSubmit={save} className="min-w-0">
          <DialogHeader>
            <DialogTitle>New monthly report</DialogTitle>
            <DialogDescription>
              Starts as a draft only you can see. The metrics and evaluations for the month are
              filled in for you.
            </DialogDescription>
          </DialogHeader>

          <div className="my-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="rmonth">Month</Label>
              <Input
                id="rmonth"
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                required
              />
              {clash && (
                <p className="text-xs text-destructive">
                  {monthLabel(`${month}-01`)} already has a report. Open that one instead.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="rtitle">Title</Label>
              <Input
                id="rtitle"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={suggested}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="rsub">Subtitle</Label>
              <Input
                id="rsub"
                value={subtitle}
                onChange={(e) => setSubtitle(e.target.value)}
                placeholder="Optional — a line under the title"
              />
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={guard.requestClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy || clash}>
              Create draft
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
